/**
 * Widget V2 - Login View
 *
 * User authentication form.
 */

import React, { useState } from "react";
import { useNavigate, useLocation, Link as RouterLink } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Alert from "@mui/material/Alert";
import Link from "@mui/material/Link";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useSnackbar } from "notistack";

import { useWidget } from "/imports/state/WidgetContext";
import { useAuth } from "/imports/state/AuthContext";
import { useUrlFilters } from "/imports/libs/url";
import { CardBackButton, GoogleSignInButton, useGoogleSignIn } from "/imports/ui/components/shared";

/**
 * Login View component
 */
function LoginView() {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { slug, saasId } = useWidget();
  const { login, error: authError, clearError } = useAuth();
  const { filters } = useUrlFilters({});

  const [email, setEmail] = useState(location.state?.email || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { handleCode, pending: googlePending } = useGoogleSignIn({ onError: setError });

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    clearError();

    if (!email || !password) {
      setError("Please enter your email and password");
      return;
    }

    setLoading(true);
    try {
      await login(email, password, saasId);
      enqueueSnackbar("Logged in successfully!", { variant: "success" });

      // Redirect to intended page or home
      const redirect = filters.redirect || "";
      navigate(`/${slug}/${redirect}`);
    } catch (err) {
      setError(err.message || "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
      <Card sx={{ maxWidth: "25rem", width: "100%" }}>
        <CardContent sx={{ p: { xs: 2.5, sm: 4 }, "&:last-child": { pb: { xs: 2.5, sm: 4 } } }}>
          <CardBackButton onClick={() => navigate(`/${slug}`)} />
          <Typography variant="h5" fontWeight={600} textAlign="center" sx={{ mb: 3 }}>
            Sign In
          </Typography>

          {/* Error message */}
          {(error || authError) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error || authError}
            </Alert>
          )}

          <GoogleSignInButton onCode={handleCode} text="signin_with" disabled={loading || googlePending} />

          <Divider sx={{ my: 2 }}>
            <Typography variant="body2" color="textSecondary">
              or
            </Typography>
          </Divider>

          <Typography variant="body2" color="textSecondary" textAlign="center" mb={1}>
            Enter your email and password to continue
          </Typography>

          {/* Login form */}
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              autoComplete="email"
              autoFocus
            />

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              autoComplete="current-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((s) => !s)}
                      onMouseDown={(e) => e.preventDefault()}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <Box textAlign="right">
              <Link component={RouterLink} to={`/${slug}/forgot-password`} variant="body2">
                Forgot password?
              </Link>
            </Box>

            <Button type="submit" variant="contained" fullWidth size="large" disabled={loading || googlePending} sx={{ mt: 2, mb: 2 }}>
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>

          <Typography variant="body2" textAlign="center">
            New here, or need to set up your account?{" "}
            <Link
              component={RouterLink}
              to={`/${slug}/signup${filters.redirect ? `?redirect=${filters.redirect}` : ""}`}
              sx={{ whiteSpace: "nowrap" }}
            >
              Get started
            </Link>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}

export default LoginView;
